/**
 * AiConfigPage — AI 모델 provider(cloud/onprem) 런타임 설정 (ADMIN).
 *
 * 처방전 OCR / 챗봇 대상을 각각 cloud·onprem 으로 독립 선택하고, 프로필별
 * (base_url·api_key·텍스트/비전 모델)을 입력한다. 저장 즉시 백엔드 반영(재배포 불필요).
 * api_key 는 조회 시 '********' 마스킹 — 그대로 두면 미변경, 입력 시에만 갱신.
 */
import { useEffect, useState } from 'react'
import { Button, Card, Input, Radio, Select, Space, Spin, Typography, message } from 'antd'
import { medApi, toErrMsg } from './medication/api'
import { useLabel } from '@/framework/useLabel'

type Cfg = Record<string, string>

/** AI 프로필 대상 — 클라우드(Gemini/Groq 등) 또는 온프렘(Ollama). */
type AiTarget = 'cloud' | 'onprem'

// 클라우드 프리셋 — 선택 시 base_url·모델 자동 채움(키는 직접 입력). 무료티어 제공 모델 기준.
const CLOUD_PRESETS: Record<
  string,
  { base_url: string; model_text: string; model_vision: string }
> = {
  gemini: {
    base_url: 'https://generativelanguage.googleapis.com/v1beta/openai/',
    model_text: 'gemini-2.5-flash',
    model_vision: 'gemini-2.5-flash'
  },
  openrouter: {
    base_url: 'https://openrouter.ai/api/v1',
    model_text: 'google/gemini-2.0-flash-exp:free',
    model_vision: 'meta-llama/llama-4-maverick:free'
  }
}

/** AI provider 런타임 설정 화면 — cloud/onprem 프로필별 base_url·모델·키 관리(ADMIN). */
export default function AiConfigPage() {
  const { t } = useLabel()
  const [cfg, setCfg] = useState<Cfg>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      setCfg((await medApi.adminAiConfig.get()) ?? {})
    } catch (e) {
      message.error(toErrMsg(e))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
  }, [])

  const set = (k: string, v: string) => setCfg((p) => ({ ...p, [k]: v }))

  const save = async () => {
    setSaving(true)
    try {
      await medApi.adminAiConfig.update(cfg)
      message.success(t('MSG_AI_CONFIG_SAVED', 'AI 설정이 저장되었습니다'))
      await load()
    } catch (e) {
      message.error(toErrMsg(e))
    } finally {
      setSaving(false)
    }
  }

  const targetRadio = (key: string, label: string) => (
    <div style={{ marginBottom: 16 }}>
      <Typography.Text strong>{label}</Typography.Text>
      <div style={{ marginTop: 4 }}>
        <Radio.Group value={cfg[key] || 'onprem'} onChange={(e) => set(key, e.target.value)}>
          <Radio.Button value="cloud">{t('VAL_AI_CLOUD', '클라우드')}</Radio.Button>
          <Radio.Button value="onprem">{t('VAL_AI_ONPREM', '온프레미스')}</Radio.Button>
        </Radio.Group>
      </div>
    </div>
  )

  // 클라우드 프리셋 적용 — base_url·모델만 채움(api_key 는 직접).
  const applyPreset = (p: string) => {
    const pr = CLOUD_PRESETS[p]
    if (!pr) return
    setCfg((prev) => ({
      ...prev,
      'ai.cloud.base_url': pr.base_url,
      'ai.cloud.model_text': pr.model_text,
      'ai.cloud.model_vision': pr.model_vision
    }))
  }

  const profileCard = (profile: AiTarget, title: string) => (
    <Card size="small" title={title} style={{ marginBottom: 12, borderRadius: 10 }}>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        {profile === 'cloud' && (
          <div>
            <Typography.Text type="secondary">
              {t('LBL_AI_PRESET', '클라우드 프리셋')}
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              placeholder={t('VAL_AI_PRESET_CUSTOM', '직접 입력')}
              onChange={applyPreset}
              options={[
                { value: 'gemini', label: t('VAL_AI_PRESET_GEMINI', 'Google Gemini (무료)') },
                { value: 'openrouter', label: t('VAL_AI_PRESET_OPENROUTER', 'OpenRouter (무료)') }
              ]}
            />
          </div>
        )}
        <div>
          <Typography.Text type="secondary">
            {t('LBL_AI_BASE_URL', 'Base URL (OpenAI 호환 /v1)')}
          </Typography.Text>
          <Input
            value={cfg[`ai.${profile}.base_url`] ?? ''}
            placeholder="https://api.openai.com/v1"
            onChange={(e) => set(`ai.${profile}.base_url`, e.target.value)}
          />
        </div>
        <div>
          <Typography.Text type="secondary">{t('LBL_AI_API_KEY', 'API Key')}</Typography.Text>
          <Input.Password
            value={cfg[`ai.${profile}.api_key`] ?? ''}
            placeholder="sk-..."
            onChange={(e) => set(`ai.${profile}.api_key`, e.target.value)}
          />
        </div>
        <div>
          <Typography.Text type="secondary">
            {t('LBL_AI_MODEL_TEXT', '텍스트 모델 (챗봇)')}
          </Typography.Text>
          <Input
            value={cfg[`ai.${profile}.model_text`] ?? ''}
            placeholder="gpt-4o-mini / gemma-4-12b-it"
            onChange={(e) => set(`ai.${profile}.model_text`, e.target.value)}
          />
        </div>
        <div>
          <Typography.Text type="secondary">
            {t('LBL_AI_MODEL_VISION', '비전 모델 (OCR)')}
          </Typography.Text>
          <Input
            value={cfg[`ai.${profile}.model_vision`] ?? ''}
            placeholder="gpt-4o / gemma-4-12b-it"
            onChange={(e) => set(`ai.${profile}.model_vision`, e.target.value)}
          />
        </div>
      </Space>
    </Card>
  )

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: 16 }}>
      <Typography.Title level={4} style={{ marginBottom: 4 }}>
        {t('LBL_AI_CONFIG_TITLE', 'AI 모델 설정')}
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        {t(
          'LBL_AI_CONFIG_DESC',
          '처방전 OCR과 챗봇의 AI 모델을 클라우드/온프레미스로 각각 선택합니다.'
        )}
      </Typography.Paragraph>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Card size="small" style={{ marginBottom: 12, borderRadius: 10 }}>
            {targetRadio('ai.ocr.target', t('LBL_AI_OCR_TARGET', '처방전 OCR 대상'))}
            {targetRadio('ai.chat.target', t('LBL_AI_CHAT_TARGET', '챗봇 대상'))}
          </Card>
          {profileCard('cloud', t('LBL_AI_CLOUD_PROFILE', '클라우드 프로필'))}
          {profileCard('onprem', t('LBL_AI_ONPREM_PROFILE', '온프레미스 프로필'))}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('LBL_AI_BLANK_HINT', '비워두면 서버 환경설정 기본값을 사용합니다.')}
          </Typography.Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" loading={saving} onClick={save}>
              {t('BTN_AI_SAVE', '저장')}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
